import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { GamificationState } from '@/types/gamification';
import { useGamification as useLocalGamification } from './useGamification';
import { useToast } from '@/hooks/use-toast';

export const useGamificationDB = () => {
  const localGamification = useLocalGamification();
  const [loaded, setLoaded] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const { toast } = useToast();

  // Load gamification data from database on mount
  useEffect(() => {
    loadFromDatabase();
  }, []);

  // Sync to database whenever data changes (after initial load)
  useEffect(() => {
    if (loaded && userId) {
      syncToDatabase();
    }
  }, [loaded, userId, localGamification.badges, localGamification.progress, localGamification.streak, localGamification.quizScores, localGamification.totalPoints]);

  const loadFromDatabase = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoaded(true);
        return;
      }

      setUserId(user.id);

      const { data, error } = await supabase
        .from('gamification_data')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error loading gamification data:', error);
        setLoaded(true);
        return;
      }

      // If data exists in DB, it takes precedence over localStorage
      if (data) {
        // We need to update the local state with DB data
        // This is a bit tricky since useGamification uses localStorage
        // For now, we'll just track that it's loaded
        console.log('Loaded gamification data from database:', data);
      }

      // Check and award daily login points
      await checkDailyLogin(user.id);
      
      setLoaded(true);
    } catch (error) {
      console.error('Error in loadFromDatabase:', error);
      setLoaded(true);
    }
  };

  const checkDailyLogin = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('gamification_data')
        .select('streak, total_points, updated_at')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error checking daily login:', error);
        return;
      }

      const today = new Date().toDateString();
      const lastUpdate = data?.updated_at ? new Date(data.updated_at).toDateString() : null;

      // Award points if this is their first login today
      if (lastUpdate !== today) {
        const streakData = (data?.streak as any) || { currentStreak: 0, lastActivityDate: null };
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toDateString();

        const isConsecutive = streakData.lastActivityDate === yesterdayStr;
        const newStreak = isConsecutive ? (streakData.currentStreak || 0) + 1 : 1;

        // Award 5 points for daily login
        localGamification.updateStreak();
        
        toast({
          title: "Daily Login Bonus! 🎉",
          description: `+5 points! Current streak: ${newStreak} day${newStreak !== 1 ? 's' : ''}`,
        });
      }
    } catch (error) {
      console.error('Error in checkDailyLogin:', error);
    }
  };

  const syncToDatabase = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('gamification_data')
        .upsert({
          user_id: user.id,
          badges: localGamification.badges as any,
          progress: localGamification.progress as any,
          streak: localGamification.streak as any,
          quiz_scores: localGamification.quizScores as any,
          total_points: localGamification.totalPoints,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });
    } catch (error) {
      console.error('Error syncing gamification:', error);
    }
  };

  return {
    ...localGamification,
    loaded,
    userId
  };
};
